import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CalendarCheck,
  CheckCircle2,
  ClipboardList,
  FileText,
  MapPin,
  RefreshCcw,
  Search,
  User as UserIcon,
  Wrench,
  X,
} from 'lucide-react';
import {
  agendamentoAPI,
  type AgendamentoPeriodo,
  type MotivoOS,
  type OrdemServicoERP,
  type PopERP,
  type Tecnico,
} from '../../services/api';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Modal,
  MultiSelect,
  PageHeader,
  Pagination,
  Select,
  Spinner,
  StatusBadge,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  Tabs,
  type Tone,
} from '../../components/ui';

/**
 * Agendamento > Ordens de Serviço (Fase 35).
 *
 * Fila das OS do ERP (filtradas pela data de CADASTRO) pra o operador
 * escolher técnico e agendar — a mesma operação do modal da Agenda, só que
 * partindo da OS em vez do cliente. Agendar reagenda a OS no ERP e espelha
 * na Agenda local, então ela aparece também em "Por técnico".
 */

const POR_PAGINA = 50;
const SEM_RESPONSAVEL = '__sem__';

type StatusOS = 0 | 1 | 2;
type Visao = 'todas' | 'sem' | 'agendadas';

const STATUS_OPCOES: Array<{ value: StatusOS; label: string }> = [
  { value: 0, label: 'Abertas' },
  { value: 1, label: 'Em execução' },
  { value: 2, label: 'Finalizadas' },
];

const STATUS_TONE: Record<number, Tone> = { 0: 'warning', 1: 'primary', 2: 'success' };

function isoDia(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** "2026-09-21" (ou com hora) → "21/09/2026". Não passa por Date — sem fuso. */
function dataBR(iso?: string | null): string {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

function cidadeDoPop(pop?: string): string {
  return (pop ?? '').replace(/\s*-\s*[A-Za-z]{2}\s*$/, '').trim().toUpperCase();
}

/** OS já tem data — no ERP ou na Agenda do painel. */
function estaAgendada(o: OrdemServicoERP): boolean {
  return !!o.data_agendamento || !!o.agendamentoLocal;
}

function podeAgendar(o: OrdemServicoERP): boolean {
  return o.status_id === 0 || o.status_id === 1;
}

function errorMessage(err: unknown): string {
  const e = err as { response?: { data?: { message?: string } }; message?: string };
  return e.response?.data?.message ?? e.message ?? 'Erro desconhecido';
}

export default function OrdensServico() {
  // Filtros enviados ao ERP
  const [dataInicio, setDataInicio] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return isoDia(d);
  });
  const [dataFim, setDataFim] = useState(() => isoDia(new Date()));
  const [status, setStatus] = useState<StatusOS>(0);
  const [pops, setPops] = useState<string[]>([]);
  const [motivos, setMotivos] = useState<string[]>([]);

  // Filtros locais (sobre o resultado)
  const [responsaveis, setResponsaveis] = useState<string[]>([]);
  const [busca, setBusca] = useState('');
  const [visao, setVisao] = useState<Visao>('todas');
  const [pagina, setPagina] = useState(1);

  // Dados
  const [opcoes, setOpcoes] = useState<{ motivos: MotivoOS[]; pops: PopERP[] } | null>(null);
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [ordens, setOrdens] = useState<OrdemServicoERP[]>([]);
  const [total, setTotal] = useState(0);
  const [truncado, setTruncado] = useState(false);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  const [selecionada, setSelecionada] = useState<OrdemServicoERP | null>(null);

  // Motivos/POPs (cacheados no backend) + técnicos: carregam uma vez
  useEffect(() => {
    agendamentoAPI.ordensServico
      .filtros()
      .then(setOpcoes)
      .catch((err) => setErro(errorMessage(err)));
    agendamentoAPI.tecnicos
      .list({ active: true })
      .then(setTecnicos)
      .catch(() => setTecnicos([]));
  }, []);

  const carregar = useCallback(async () => {
    if (!dataInicio || !dataFim) return;
    setLoading(true);
    setErro(null);
    try {
      const r = await agendamentoAPI.ordensServico.list({
        dataInicio,
        dataFim,
        status,
        pop: pops.map(Number),
        motivo: motivos.map(Number),
      });
      setOrdens(r.ordens);
      setTotal(r.total);
      setTruncado(r.truncado);
    } catch (err) {
      setOrdens([]);
      setTotal(0);
      setErro(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [dataInicio, dataFim, status, pops, motivos]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  useEffect(() => setPagina(1), [responsaveis, busca, visao, ordens]);

  // Responsáveis vêm do próprio resultado — o ERP não filtra por técnico
  const responsavelOpcoes = useMemo(() => {
    const nomes = new Set<string>();
    let temSem = false;
    for (const o of ordens) {
      const r = (o.responsavel ?? '').trim();
      if (r) nomes.add(r);
      else temSem = true;
    }
    const lista = [...nomes].sort((a, b) => a.localeCompare(b)).map((n) => ({ value: n, label: n }));
    return temSem ? [{ value: SEM_RESPONSAVEL, label: 'Sem responsável' }, ...lista] : lista;
  }, [ordens]);

  const filtradasSemVisao = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return ordens.filter((o) => {
      if (responsaveis.length > 0) {
        const r = (o.responsavel ?? '').trim() || SEM_RESPONSAVEL;
        if (!responsaveis.includes(r)) return false;
      }
      if (!termo) return true;
      return [o.cliente, o.contrato, o.id, o.ocorrencia].some((v) =>
        String(v ?? '').toLowerCase().includes(termo),
      );
    });
  }, [ordens, responsaveis, busca]);

  const contagem = useMemo(() => {
    const agendadas = filtradasSemVisao.filter(estaAgendada).length;
    return { todas: filtradasSemVisao.length, agendadas, sem: filtradasSemVisao.length - agendadas };
  }, [filtradasSemVisao]);

  const filtradas = useMemo(() => {
    if (visao === 'sem') return filtradasSemVisao.filter((o) => !estaAgendada(o));
    if (visao === 'agendadas') return filtradasSemVisao.filter(estaAgendada);
    return filtradasSemVisao;
  }, [filtradasSemVisao, visao]);

  const paginas = Math.max(1, Math.ceil(filtradas.length / POR_PAGINA));
  const visiveis = filtradas.slice((pagina - 1) * POR_PAGINA, pagina * POR_PAGINA);

  const filtrosAtivos =
    pops.length + motivos.length + responsaveis.length > 0 || !!busca || status !== 0;

  function limparFiltros(): void {
    setPops([]);
    setMotivos([]);
    setResponsaveis([]);
    setBusca('');
    setStatus(0);
  }

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <PageHeader
        icon={<ClipboardList size={22} />}
        title="Ordens de Serviço"
        description={
          loading
            ? 'Consultando o ERP…'
            : `${filtradas.length} de ${total} OS · cadastradas entre ${dataBR(dataInicio)} e ${dataBR(dataFim)}`
        }
        actions={
          <Button
            leadingIcon={<RefreshCcw size={15} className={loading ? 'animate-spin' : ''} />}
            onClick={() => void carregar()}
            disabled={loading}
          >
            Atualizar
          </Button>
        }
      />

      {/* Filtros */}
      <Card className="mb-4 flex flex-wrap items-end gap-3 p-4">
        <Filtro label="Cadastro de">
          <Input
            size="sm"
            type="date"
            value={dataInicio}
            max={dataFim}
            onChange={(e) => setDataInicio(e.target.value)}
          />
        </Filtro>
        <Filtro label="Até">
          <Input
            size="sm"
            type="date"
            value={dataFim}
            min={dataInicio}
            onChange={(e) => setDataFim(e.target.value)}
          />
        </Filtro>
        <Filtro label="Status">
          <Select
            size="sm"
            value={status}
            onChange={(e) => setStatus(Number(e.target.value) as StatusOS)}
          >
            {STATUS_OPCOES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </Select>
        </Filtro>
        <Filtro label="Cidade (POP)">
          <MultiSelect
            options={(opcoes?.pops ?? []).map((p) => ({ value: String(p.id), label: p.pop }))}
            selected={pops}
            onChange={setPops}
            placeholder={opcoes ? 'Todas' : 'Carregando…'}
            plural="cidades"
          />
        </Filtro>
        <Filtro label="Motivo">
          <MultiSelect
            options={(opcoes?.motivos ?? []).map((m) => ({
              value: String(m.id),
              label: m.descricao,
            }))}
            selected={motivos}
            onChange={setMotivos}
            placeholder={opcoes ? 'Todos' : 'Carregando…'}
            plural="motivos"
          />
        </Filtro>
        <Filtro label="Responsável">
          <MultiSelect
            options={responsavelOpcoes}
            selected={responsaveis}
            onChange={setResponsaveis}
            placeholder="Todos"
            plural="técnicos"
          />
        </Filtro>
        {filtrosAtivos && (
          <Button variant="ghost" size="sm" className="ml-auto self-end" onClick={limparFiltros}>
            Limpar filtros
          </Button>
        )}
      </Card>

      <Input
        className="mb-4"
        placeholder="Buscar por cliente, contrato, nº da OS ou ocorrência…"
        value={busca}
        onChange={(e) => setBusca(e.target.value)}
        leadingIcon={<Search size={15} />}
        trailingIcon={
          busca ? (
            <button
              type="button"
              onClick={() => setBusca('')}
              className="text-muted transition-colors hover:text-fg-2"
              aria-label="Limpar busca"
            >
              <X size={15} />
            </button>
          ) : undefined
        }
      />

      {sucesso && (
        <Alert
          tone="success"
          className="mb-4"
          action={
            <Button variant="ghost" size="sm" onClick={() => setSucesso(null)}>
              Fechar
            </Button>
          }
        >
          {sucesso}
        </Alert>
      )}
      {erro && (
        <Alert tone="danger" className="mb-4">
          {erro}
        </Alert>
      )}
      {truncado && (
        <Alert tone="warning" className="mb-4">
          O ERP retornou mais de {ordens.length} OS para esse período — só as primeiras foram
          carregadas. Reduza o intervalo de datas ou filtre por cidade/motivo.
        </Alert>
      )}

      <Tabs<Visao>
        className="mb-4"
        value={visao}
        onChange={setVisao}
        items={[
          { value: 'todas', label: 'Todas', count: contagem.todas },
          { value: 'sem', label: 'Sem agendamento', count: contagem.sem },
          { value: 'agendadas', label: 'Agendadas', count: contagem.agendadas },
        ]}
      />

      {!loading && filtradas.length === 0 ? (
        <EmptyState
          icon={<ClipboardList size={20} />}
          title="Nenhuma OS encontrada"
          description="Nenhuma ordem de serviço para os filtros selecionados."
        />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>OS</TH>
              <TH>Cadastro</TH>
              <TH>Cliente</TH>
              <TH>Motivo</TH>
              <TH>Cidade</TH>
              <TH>Responsável</TH>
              <TH>Agendamento</TH>
              <TH>Status</TH>
            </TR>
          </THead>
          <TBody>
            {loading ? (
              <TR>
                <TD colSpan={8} align="center" className="py-12">
                  <Spinner className="mx-auto" size={20} />
                </TD>
              </TR>
            ) : (
              visiveis.map((o) => (
                <TR key={o.id} interactive onClick={() => setSelecionada(o)}>
                  <TD numeric className="text-xs text-fg">
                    {o.id}
                  </TD>
                  <TD className="whitespace-nowrap text-xs">{dataBR(o.data_cadastro)}</TD>
                  <TD className="max-w-[16rem]">
                    <div className="truncate font-medium text-fg" title={o.cliente}>
                      {o.cliente || '—'}
                    </div>
                    <div className="font-mono text-[11.5px] text-muted">
                      Contrato {o.contrato ?? '—'}
                    </div>
                  </TD>
                  <TD className="max-w-[14rem] truncate text-xs" title={o.motivo}>
                    {o.motivo || '—'}
                  </TD>
                  <TD className="text-xs">{cidadeDoPop(o.pop) || '—'}</TD>
                  <TD className="text-xs">
                    {o.responsavel?.trim() || <span className="text-faint">—</span>}
                  </TD>
                  <TD className="whitespace-nowrap text-xs">
                    {o.agendamentoLocal ? (
                      <span className="inline-flex items-center gap-1 text-success">
                        <CalendarCheck size={13} />
                        {dataBR(o.agendamentoLocal.data)}
                      </span>
                    ) : o.data_agendamento ? (
                      dataBR(o.data_agendamento)
                    ) : (
                      <span className="text-faint">—</span>
                    )}
                  </TD>
                  <TD>
                    <StatusBadge tone={STATUS_TONE[o.status_id ?? 0] ?? 'neutral'}>
                      {o.status || STATUS_OPCOES.find((s) => s.value === o.status_id)?.label}
                    </StatusBadge>
                  </TD>
                </TR>
              ))
            )}
          </TBody>
        </Table>
      )}

      {!loading && paginas > 1 && (
        <Pagination
          className="mt-4"
          page={pagina}
          pageCount={paginas}
          total={filtradas.length}
          pageSize={POR_PAGINA}
          onChange={setPagina}
        />
      )}

      {selecionada && (
        <AgendarOsModal
          os={selecionada}
          tecnicos={tecnicos}
          onClose={() => setSelecionada(null)}
          onAgendada={(msg) => {
            setSelecionada(null);
            setSucesso(msg);
            void carregar();
          }}
        />
      )}
    </div>
  );
}

function Filtro({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[12.5px] font-medium text-fg-2">{label}</span>
      {children}
    </label>
  );
}

// ============================================================
// Modal — agendar a OS (técnico + data)
// ============================================================

function AgendarOsModal({
  os,
  tecnicos,
  onClose,
  onAgendada,
}: {
  os: OrdemServicoERP;
  tecnicos: Tecnico[];
  onClose: () => void;
  onAgendada: (mensagem: string) => void;
}) {
  const cidade = cidadeDoPop(os.pop);
  const editavel = podeAgendar(os);

  // Técnicos da cidade primeiro; sem ninguém cadastrado nela, mostra todos.
  // Sem usuário do ERP não dá pra agendar (o SGP identifica pelo username).
  const { opcoesTecnico, semUsuario } = useMemo(() => {
    const daCidade = tecnicos.filter((t) => t.cidades.length === 0 || t.cidades.includes(cidade));
    const base = daCidade.length > 0 ? daCidade : tecnicos;
    return {
      opcoesTecnico: base.filter((t) => !!t.username),
      semUsuario: base.filter((t) => !t.username).length,
    };
  }, [tecnicos, cidade]);

  const amanha = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return isoDia(d);
  }, []);

  const inicial = os.agendamentoLocal?.data?.slice(0, 10) || os.data_agendamento?.slice(0, 10);
  const tecnicoAtual = opcoesTecnico.find(
    (t) =>
      t.nome === os.agendamentoLocal?.equipe ||
      (!!os.responsavel && (t.username === os.responsavel || t.nome === os.responsavel)),
  );

  const [tecnicoId, setTecnicoId] = useState(tecnicoAtual?.id ?? '');
  const [data, setData] = useState(inicial && inicial >= isoDia(new Date()) ? inicial : amanha);
  const [periodo, setPeriodo] = useState<AgendamentoPeriodo>(
    os.agendamentoLocal?.periodo ?? 'manha',
  );
  const [hora, setHora] = useState(periodo === 'tarde' ? '13:00' : '08:00');
  const [obs, setObs] = useState('');
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function handleAgendar(): Promise<void> {
    if (!editavel || saving) return;
    if (!tecnicoId) {
      setErro('Selecione o técnico.');
      return;
    }
    if (!data) {
      setErro('Selecione a data.');
      return;
    }
    setErro(null);
    setSaving(true);
    try {
      const r = await agendamentoAPI.ordensServico.agendar(os.id, {
        tecnicoId,
        data,
        periodo,
        hora,
        contratoErpId: Number(os.contrato),
        cliente: os.cliente ?? '',
        pop: os.pop ?? '',
        motivo: os.motivo ?? '',
        observacaoOperador: obs.trim() || undefined,
      });
      const tecnico = opcoesTecnico.find((t) => t.id === tecnicoId);
      onAgendada(
        `OS ${os.id} agendada para ${dataBR(data)} (${periodo === 'manha' ? 'manhã' : 'tarde'}) com ${tecnico?.nome ?? 'o técnico'}. ${r.message}`,
      );
    } catch (err) {
      setErro(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      onConfirm={editavel ? () => void handleAgendar() : undefined}
      title={`OS ${os.id}`}
      description={os.motivo}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            {editavel ? 'Cancelar' : 'Fechar'}
          </Button>
          {editavel && (
            <Button
              leadingIcon={<CalendarCheck size={15} />}
              loading={saving}
              disabled={opcoesTecnico.length === 0}
              onClick={() => void handleAgendar()}
            >
              Agendar OS
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-5">
        {/* Dados da OS (ERP) */}
        <div className="flex flex-col gap-1.5">
          <Info icon={<UserIcon size={14} />} label="Cliente" value={os.cliente} />
          <Info
            icon={<FileText size={14} />}
            label="Contrato"
            value={
              <span className="inline-flex flex-wrap items-center gap-2">
                <span className="font-mono">{os.contrato}</span>
                {os.contrato_status && <Badge tone="neutral">{os.contrato_status}</Badge>}
              </span>
            }
          />
          <Info icon={<MapPin size={14} />} label="Cidade" value={os.pop} />
          <Info icon={<Wrench size={14} />} label="Status" value={os.status} />
          <Info icon={<FileText size={14} />} label="Cadastro" value={dataBR(os.data_cadastro)} />
          <Info
            icon={<UserIcon size={14} />}
            label="Responsável"
            value={os.responsavel?.trim() || 'Sem responsável'}
          />
          {os.ocorrencia && (
            <Info icon={<FileText size={14} />} label="Ocorrência" value={os.ocorrencia} />
          )}
        </div>

        {os.conteudo?.trim() && (
          <div className="whitespace-pre-wrap rounded-lg border border-line bg-sunken px-3 py-2.5 text-[13px] text-fg-2">
            {os.conteudo}
          </div>
        )}

        {os.agendamentoLocal && (
          <Alert tone="info" icon={<CalendarCheck size={17} />}>
            Já está na Agenda em {dataBR(os.agendamentoLocal.data)} (
            {os.agendamentoLocal.periodo === 'manha' ? 'manhã' : 'tarde'}) com{' '}
            {os.agendamentoLocal.equipe}. Agendar de novo atualiza esse agendamento.
          </Alert>
        )}

        {!editavel ? (
          <Alert tone="neutral" icon={<CheckCircle2 size={17} />}>
            OS finalizada — não pode ser agendada.
          </Alert>
        ) : (
          <div className="flex flex-col gap-4 border-t border-line pt-5">
            <Field
              label="Técnico"
              required
              hint={
                opcoesTecnico.length === 0
                  ? undefined
                  : semUsuario > 0
                    ? `${semUsuario} técnico(s) sem usuário do ERP ficaram de fora — sincronize em Configurações > Técnicos.`
                    : cidade
                      ? `Técnicos que atendem ${cidade}.`
                      : undefined
              }
            >
              {opcoesTecnico.length === 0 ? (
                <Alert tone="warning">
                  Nenhum técnico com usuário do ERP disponível. Sincronize os técnicos em
                  Agendamento &gt; Configurações.
                </Alert>
              ) : (
                <Select value={tecnicoId} onChange={(e) => setTecnicoId(e.target.value)}>
                  <option value="">Selecione…</option>
                  {opcoesTecnico.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nome}
                      {t.equipe ? ` (${t.equipe})` : ''}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <div className="grid grid-cols-3 gap-3">
              <Field label="Data" required>
                <Input
                  type="date"
                  value={data}
                  min={isoDia(new Date())}
                  onChange={(e) => setData(e.target.value)}
                />
              </Field>
              <Field label="Período">
                <Select
                  value={periodo}
                  onChange={(e) => {
                    const p = e.target.value as AgendamentoPeriodo;
                    setPeriodo(p);
                    setHora(p === 'tarde' ? '13:00' : '08:00');
                  }}
                >
                  <option value="manha">Manhã</option>
                  <option value="tarde">Tarde</option>
                </Select>
              </Field>
              <Field label="Horário">
                <Input type="time" value={hora} onChange={(e) => setHora(e.target.value)} />
              </Field>
            </div>

            <Field
              label="Observação da agenda"
              hint={'Opcional — aparece na coluna Observação da visão "Por técnico". Não vai pro ERP.'}
            >
              <Input
                value={obs}
                onChange={(e) => setObs(e.target.value)}
                placeholder="ex: ir antes das 14h, ligar ao chegar…"
                maxLength={200}
              />
            </Field>

            {erro && <Alert tone="danger">{erro}</Alert>}
          </div>
        )}
      </div>
    </Modal>
  );
}

function Info({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2 text-[13.5px]">
      <span className="mt-0.5 flex w-28 flex-none items-center gap-1.5 text-[11.5px] font-medium uppercase tracking-[0.04em] text-muted">
        {icon}
        {label}
      </span>
      <span className="min-w-0 break-words text-fg">{value || '—'}</span>
    </div>
  );
}
